import React, { useRef, useState } from "react";
import styles from './DocumentProcessor.module.css';

import TabsSwitcher from "./TabsSwitcher";
import InputModeSwitcher from "./InputModeSwitcher";
import TextInputPanel from "./TextInputPanel";
import ExcelInputPanel from "./ExcelInputPanel";
import TopControls from "./TopControls";
import ResultPanel from "./ResultPanel";

import { parseCSV } from "./parseUtils";
import {
    getDocumentProcessorEndpoints,
    processTextRequest,
    processExcelRequest,
    saveDocumentToDB
} from "../../api/documentProcessorApi";
import { fetchOffers } from "../../api/offersApi";
import { fetchOrders } from "../../api/ordersApi";
import {
    hydrateProcessedResult,
    serializeTableToCSV
} from "../../utils/documentProcessorHydrators";

const toPositiveNumber = (value, fallback = 0) => {
    const numeric = Number(value);
    return Number.isFinite(numeric) && numeric >= 0 ? numeric : fallback;
};

const countTextRows = (text) => {
    if (!text) {
        return 0;
    }

    return text
        .split(/\r?\n/)
        .map(line => line.trim())
        .filter(Boolean).length;
};

const DocumentProcessor = ({
    setOrders,
    setOffers,
    initialTab = "request",
    lockedDocumentType = null,
    catalogAppearance = false,
    linkedOrderId = null,
    fileNamePrefix = "",
    onDocumentSaved
}) => {

    const config = {
        request: {
            ...getDocumentProcessorEndpoints("request"),
            placeholder: "Вставьте текст запроса...",
            tabName: "Обработать запрос"
        },
        offer: {
            ...getDocumentProcessorEndpoints("offer"),
            placeholder: "Вставьте текст предложения...",
            tabName: "Обработать предложение"
        }
    };

    // 🔥 САМОЕ ГЛАВНОЕ — отдельные состояния для каждой вкладки!
    const [tabs, setTabs] = useState({
        request: {
            inputMode: "text",
            inputText: "",
            selectedFile: null,
            fileName: "",
            processedText: "",
            processedTable: { headers: [], data: [] },
            sourceWorkbookBase64: ""
        },
        offer: {
            inputMode: "text",
            inputText: "",
            selectedFile: null,
            fileName: "",
            processedText: "",
            processedTable: { headers: [], data: [] },
            sourceWorkbookBase64: ""
        }
    });

    const [activeTab, setActiveTab] = useState(lockedDocumentType || initialTab);
    const [loading, setLoading] = useState(false);
    const [saveStatus, setSaveStatus] = useState(null);
    const [processingError, setProcessingError] = useState(null);
    const [processingInfo, setProcessingInfo] = useState(null);
    const processingInFlightRef = useRef(false);

    const current = config[activeTab];
    const tab = tabs[activeTab];
    const ensureFileNamePrefix = (fileName) => {
        const normalizedPrefix = String(fileNamePrefix || "").trim();
        const normalizedFileName = String(fileName || "").trim();

        if (!normalizedPrefix || !normalizedFileName) {
            return normalizedFileName;
        }

        return normalizedFileName.toLowerCase().startsWith(`${normalizedPrefix.toLowerCase()}_`)
            ? normalizedFileName
            : `${normalizedPrefix}_${normalizedFileName}`;
    };

    // ---------- UPDATE HELPERS ----------
    const updateTab = (patch) => {
        setTabs(prev => ({
            ...prev,
            [activeTab]: { ...prev[activeTab], ...patch }
        }));
    };

    // ---------- SUBMIT ----------
    const runProcessing = async () => {
        const processingStartedAt = performance.now();
        let response =
            tab.inputMode === "text"
                ? await processTextRequest(current.textApi, tab.inputText)
                : await processExcelRequest(current.excelApi, tab.selectedFile);
        const durationSeconds = (performance.now() - processingStartedAt) / 1000;

        const { table, text } = hydrateProcessedResult(response);
        const processedRows = Array.isArray(table.data) ? table.data.length : 0;
        const submittedRowsFallback = tab.inputMode === "text" ? countTextRows(tab.inputText) : processedRows;

        updateTab({
            processedText: text,
            processedTable: table,
            sourceWorkbookBase64: tab.inputMode === "excel" ? response.original || "" : ""
        });

        setProcessingInfo({
            submittedRows: toPositiveNumber(response.submittedRows, submittedRowsFallback),
            processedRows: toPositiveNumber(response.processedRows, processedRows),
            durationSeconds
        });
    };

    const handleSubmit = async () => {
        if (processingInFlightRef.current) {
            return;
        }

        processingInFlightRef.current = true;
        try {
            setLoading(true);
            setSaveStatus(null);
            setProcessingError(null);
            setProcessingInfo(null);
            updateTab({ processedText: "Обработка..." });

            await runProcessing();

        } catch (e) {
            const message = e.message || "Не удалось обработать документ";
            updateTab({
                processedText: "Ошибка: " + message,
                processedTable: { headers: [], data: [] }
            });
            setProcessingError({
                message,
                failedRows: Array.isArray(e.failedRows) ? e.failedRows : [],
                partialProcessed: e.partialProcessed || ""
            });
        } finally {
            processingInFlightRef.current = false;
            setLoading(false);
        }
    };

    const handleRetryProcessing = () => {
        setProcessingError(null);
        handleSubmit();
    };

    const handleCloseProcessingError = () => {
        setProcessingError(null);
    };

    const handleCloseProcessingInfo = () => {
        setProcessingInfo(null);
    };

    const handleUpdateProcessedTable = (nextTable) => {
        updateTab({
            processedTable: nextTable,
            processedText: serializeTableToCSV(nextTable)
        });
    };

    const handleUsePartialResult = () => {
        if (!processingError?.partialProcessed) {
            setProcessingError(prev => prev ? {
                ...prev,
                message: `${prev.message}\nЧастично обработанных строк нет, загрузить документ без проблемных строк невозможно.`
            } : prev);
            return;
        }

        const table = parseCSV(processingError.partialProcessed);
        updateTab({
            processedText: processingError.partialProcessed,
            processedTable: table,
            sourceWorkbookBase64: ""
        });
        setProcessingError(null);
    };

    // ---------- CLEAR ----------
    const handleClear = () => {
        updateTab({
            inputText: "",
            selectedFile: null,
            processedText: "",
            processedTable: { headers: [], data: [] },
            fileName: "",
            sourceWorkbookBase64: ""
        });
        setSaveStatus(null);
        setProcessingError(null);
        setProcessingInfo(null);
    };
    // ---------- SAVE ----------
    const handleSave = async (binaryFile = null, processedTableOverride = null) => {
        try {
            setSaveStatus({ loading: true });
            const savedFileName = ensureFileNamePrefix(tab.fileName);

            const savedDocument = await saveDocumentToDB(current.saveApi, {
                fileName: savedFileName,
                processedTable: processedTableOverride || tab.processedTable,
                rawText: tab.inputMode === "text" ? tab.inputText : null,
                binaryFile,
                orderId: activeTab === "offer" ? linkedOrderId : null
            });

            // После успешного сохранения — перезагружаем списки
            if (activeTab === "request" && setOrders) {
                const orders = await fetchOrders();
                setOrders(orders);
            }

            if (activeTab === "offer" && setOffers) {
                const offers = await fetchOffers();
                setOffers(offers);
            }

            setSaveStatus({ ok: true, message: "Файл сохранён!" });
            await onDocumentSaved?.(savedDocument);

        } catch (e) {
            setSaveStatus({ error: true, message: e.message });
        }
    };


    // ---------- RENDER ----------
    return (
        <div className={`${styles.docTabContainer} ${catalogAppearance ? styles.catalogAppearance : ""}`}>
            {!lockedDocumentType && (
                <TabsSwitcher config={config} activeTab={activeTab} setActiveTab={setActiveTab} />
            )}

            {/* <div className={styles.processingNotice}>
                Наблюдаются сбои в обработке документов - проверяйте список обработанных строк
            </div> */}

            <div className={styles.twoColumnLayout}>

                {/* LEFT PANEL */}
                <div className={styles.leftPanel}>

                    <InputModeSwitcher
                        catalogAppearance={catalogAppearance}
                        inputMode={tab.inputMode}
                        setInputMode={(m) => updateTab({ inputMode: m })}
                    />

                    {tab.inputMode === "text" ? (
                        <TextInputPanel
                            value={tab.inputText}
                            onChange={(v) => updateTab({ inputText: v })}
                            placeholder={current.placeholder}
                        />
                    ) : (
                        <ExcelInputPanel
                            selectedFile={tab.selectedFile}
                            setSelectedFile={(f) => updateTab({ selectedFile: f })}
                            setFileName={(v) => updateTab({ fileName: ensureFileNamePrefix(v) })}
                        />
                    )}

                    <TopControls
                        catalogAppearance={catalogAppearance}
                        onSubmit={handleSubmit}
                        onClear={handleClear}
                        disabled={loading}
                        inputMode={tab.inputMode}
                        inputText={tab.inputText}
                        selectedFile={tab.selectedFile}
                    />
                </div>

                {/* RIGHT PANEL */}
                <div className={styles.rightPanel}>
                    <ResultPanel
                        catalogAppearance={catalogAppearance}
                        loading={loading}
                        processedText={tab.processedText}
                        processedTable={tab.processedTable}
                        fileName={tab.fileName}
                        setFileName={(v) => updateTab({ fileName: v })}
                        onSave={handleSave}
                        saveStatus={saveStatus}
                        sourceWorkbookBase64={tab.sourceWorkbookBase64}
                        onTableChange={handleUpdateProcessedTable}
                        documentType={activeTab}
                    />
                </div>

            </div>

            {processingError && (
                <div className={styles.processingErrorBackdrop}>
                    <div className={styles.processingErrorModal}>
                        <h3>Ошибка обработки документа</h3>
                        <p>{processingError.message}</p>
                        {processingError.failedRows.length > 0 && (
                            <div className={styles.failedRowsBox}>
                                <div className={styles.failedRowsTitle}>
                                    Неразобранные строки: {processingError.failedRows.length}
                                </div>
                                <div className={styles.failedRowsList}>
                                    {processingError.failedRows.map((row, index) => (
                                        <div key={`${index}-${row}`} className={styles.failedRow}>
                                            <span>{index + 1}</span>
                                            <code>{row}</code>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        <div className={styles.processingErrorActions}>
                            <button type="button" className={`${styles.btn} ${styles.btnBlue}`} onClick={handleRetryProcessing}>
                                Повторить обработку
                            </button>
                            <button type="button" className={styles.btn} onClick={handleUsePartialResult}>
                                Загрузить без этих строк
                            </button>
                            <button type="button" className={styles.btn} onClick={handleCloseProcessingError}>
                                Закрыть
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {processingInfo && (
                <div className={styles.processingErrorBackdrop}>
                    <div className={styles.processingInfoModal}>
                        <h3>Документ обработан</h3>
                        <p>Статистика обработки строк</p>
                        <div className={styles.processingStats}>
                            <div className={styles.processingStatItem}>
                                <span>Отправлено на обработку</span>
                                <strong>{processingInfo.submittedRows}</strong>
                            </div>
                            <div className={styles.processingStatItem}>
                                <span>Обработано</span>
                                <strong>{processingInfo.processedRows}</strong>
                            </div>
                            <div className={styles.processingStatItem}>
                                <span>Время обработки, сек</span>
                                <strong>{processingInfo.durationSeconds.toFixed(1)}</strong>
                            </div>
                        </div>
                        <div className={styles.processingErrorActions}>
                            <button type="button" className={`${styles.btn} ${styles.btnBlue}`} onClick={handleCloseProcessingInfo}>
                                ОК
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DocumentProcessor;
